import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Facebook, Linkedin, Instagram, Heart, ArrowRight } from 'lucide-react';
import { SmartLink } from './SmartLink';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: 'Personal Care', path: '/what-we-do#personal-care' },
      { name: 'Companionship', path: '/what-we-do#companionship' },
      { name: 'Daily Living Support', path: '/what-we-do#daily-living' },
      { name: 'Respite Care', path: '/what-we-do#respite-care' },
      { name: 'Medication Reminders', path: '/what-we-do#medication' },
    ],
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Why Heritage', path: '/why-heritage' },
      { name: 'Careers', path: '/careers' },
      { name: 'Home Care Guide', path: '/home-care-guide' },
      { name: 'Contact', path: '/contact' },
    ],
    resources: [
      { name: 'Who We Serve', path: '/who-we-serve' },
      { name: 'Getting Started', path: '/contact' },
      { name: 'FAQs', path: '/home-care-guide#faqs' },
    ],
  };

  return (
    <footer className="relative bg-navy text-white overflow-hidden" data-testid="main-footer">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-soft-gold/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-deep-blue/30 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />

      {/* CTA Section */}
      <div className="relative border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-14 sm:py-16 md:py-20">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium mb-4"
              >
                Ready to Experience
                <span className="text-soft-gold"> Compassionate Care?</span>
              </motion.h2>
              <p className="text-white/70 text-base sm:text-lg max-w-xl">
                Take the first step towards quality in-home care for your loved ones.
                Our team at Heritage Home Health LLC is here to help.
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contact"
                data-testid="footer-contact-cta"
                className="group flex items-center justify-center space-x-2 bg-soft-gold text-navy px-8 py-4 rounded-full text-base font-semibold hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
              >
                <span>Contact Us Today</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:+16083589578"
                data-testid="footer-phone-cta"
                className="flex items-center justify-center space-x-2 bg-white/10 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 w-full sm:w-auto"
              >
                <Phone className="w-5 h-5" />
                <span>+1 (608) 358-9578</span>
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <span className="font-serif text-xl sm:text-2xl md:text-3xl font-semibold text-white tracking-tight">
                Heritage
              </span>
              <span className="font-serif text-xl sm:text-2xl md:text-3xl font-light text-soft-gold ml-2">
                Home Health
              </span>
              <span className="block text-xs text-white/50 tracking-widest mt-1">LLC</span>
            </Link>
            <p className="text-white/70 leading-relaxed mb-6 max-w-sm">
              Heritage Home Health LLC provides compassionate, personalized in-home care
              that honors the dignity and independence of every individual we serve.
            </p>
            <div className="space-y-3">
              <a
                href="tel:+16083589578"
                className="flex items-center space-x-3 text-white/70 hover:text-soft-gold transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-soft-gold/20 transition-colors">
                  <Phone className="w-4 h-4" />
                </div>
                <span>+1 (608) 358-9578</span>
              </a>
              <a
                href="mailto:info@heritagehhealth.com"
                className="flex items-center space-x-3 text-white/70 hover:text-soft-gold transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-soft-gold/20 transition-colors">
                  <Mail className="w-4 h-4" />
                </div>
                <span>info@heritagehhealth.com</span>
              </a>
              <div className="flex items-center space-x-3 text-white/70">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </div>
                <span>4614 Crescent Rd<br />Fitchburg, WI 53711</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-sans text-sm font-bold uppercase tracking-wider text-soft-gold mb-6">
              Our Services
            </h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <SmartLink
                    to={link.path}
                    className="text-white/70 hover:text-soft-gold transition-colors text-sm"
                  >
                    {link.name}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-sans text-sm font-bold uppercase tracking-wider text-soft-gold mb-6">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <SmartLink
                    to={link.path}
                    className="text-white/70 hover:text-soft-gold transition-colors text-sm"
                  >
                    {link.name}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-sans text-sm font-bold uppercase tracking-wider text-soft-gold mb-6">
              Resources
            </h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <SmartLink
                    to={link.path}
                    className="text-white/70 hover:text-soft-gold transition-colors text-sm"
                  >
                    {link.name}
                  </SmartLink>
                </li>
              ))}
            </ul>
            {/* Social Links */}
            <div className="mt-8">
              <h3 className="font-sans text-sm font-bold uppercase tracking-wider text-soft-gold mb-4">
                Follow Us
              </h3>
              <div className="flex space-x-3">
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-soft-gold/20 transition-colors group"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4 text-white/70 group-hover:text-soft-gold" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-soft-gold/20 transition-colors group"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-4 h-4 text-white/70 group-hover:text-soft-gold" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-soft-gold/20 transition-colors group"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4 text-white/70 group-hover:text-soft-gold" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm text-center md:text-left">
              &copy; {currentYear} Heritage Home Health LLC. All rights reserved.
            </p>
            <div className="flex items-center justify-center flex-wrap gap-x-6 gap-y-2 text-sm text-white/50">
              <Link to="#" className="hover:text-soft-gold transition-colors">
                Privacy Policy
              </Link>
              <Link to="#" className="hover:text-soft-gold transition-colors">
                Terms of Service
              </Link>
              <Link to="#" className="hover:text-soft-gold transition-colors">
                Accessibility
              </Link>
            </div>
            <p className="flex items-center text-white/50 text-sm">
              Made with <Heart className="w-4 h-4 text-soft-gold mx-1" /> for families
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
